import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
} from "react";
import { useSearchParams } from "react-router-dom";
import { ACTIVE_CHAT_KEY } from "../features/chat/consts";
import {
  ConnectionStatus,
  WebSocketContext,
} from "../features/chat/context/WebSocketContext";
import { Chat } from "../features/chat/models/chat";

// Action types - simplified to single action
type ActionType = { type: "SET_ACTIVE_CHAT"; payload: Chat | null };

// Simplified state interface - we only need to track the active chat
interface ActiveChatState {
  activeChat: Chat | null;
}

// Initial state
const initialState: ActiveChatState = {
  activeChat: null,
};

// Simplified reducer
function activeChatReducer(
  state: ActiveChatState,
  action: ActionType
): ActiveChatState {
  switch (action.type) {
    case "SET_ACTIVE_CHAT":
      return {
        ...state,
        activeChat: action.payload,
      };
    default:
      return state;
  }
}

// Context interface - removed isChangingChat as we've simplified the state transitions
interface ActiveChatContextType {
  activeChat: Chat | null;
  activeChatId: string | null;
  setActiveChat: (chat: Chat | null) => void;
  updateActiveChat: (chat: Chat) => void;
  isActiveChatId: (chatId: string) => boolean;
  leaveChat: (chatId: string, permanent?: boolean) => void;
}

const ActiveChatContext = createContext<ActiveChatContextType>({
  activeChat: null,
  activeChatId: null,
  setActiveChat: () => {},
  updateActiveChat: () => {},
  isActiveChatId: () => false,
  leaveChat: () => {},
});

export const useActiveChat = () => useContext(ActiveChatContext);

export const ActiveChatProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(activeChatReducer, initialState);
  const { activeChat } = state;
  const activeChatId = activeChat?.id || null;

  const [searchParams] = useSearchParams();
  const ws = useContext(WebSocketContext);

  // Track the latest active chat ID for reconnection purposes
  const activeChatRef = useRef<string | null>(null);

  // Handle chat URL param
  const chatIdFromUrl = searchParams.get("chat_id");

  // Simplified function to change active chat - no longer async or phased
  const setActiveChat = useCallback(
    (chat: Chat | null) => {
      const newChatId = chat?.id || null;
      const currentChatId = activeChatId;

      // Skip if we're already on this chat
      if (newChatId === currentChatId) {
        return;
      }

      try {
        // Update local storage
        if (newChatId) {
          localStorage.setItem(ACTIVE_CHAT_KEY, newChatId);
          activeChatRef.current = newChatId;
        } else {
          localStorage.removeItem(ACTIVE_CHAT_KEY);
          activeChatRef.current = null;
        }

        // Handle WebSocket operations
        if (ws?.connectionStatus === ConnectionStatus.OPEN) {
          // Leave current chat if needed
          if (currentChatId) {
            console.log(`Leaving chat: ${currentChatId}`);
            ws.sendLeave(currentChatId);
          }

          // Join new chat
          if (newChatId) {
            console.log(`Joining chat: ${newChatId}`);
            ws.sendJoin(newChatId);
          }
        }

        // Update state
        dispatch({ type: "SET_ACTIVE_CHAT", payload: chat });

        // Notify components that depend on chat changes
        window.dispatchEvent(
          new CustomEvent("chat-changed", {
            detail: { chatId: newChatId }
          })
        );
      } catch (error) {
        console.error("Error changing chats:", error);
      }
    },
    [activeChatId, ws]
  );

  const updateActiveChat = useCallback((chat: Chat) => {
    if (chat.id === activeChatId) {
      // Just update the state without all the WebSocket/leaving/joining logic
      dispatch({ type: "SET_ACTIVE_CHAT", payload: chat });
      
      // Notify components that depend on chat changes
      window.dispatchEvent(
        new CustomEvent("chat-changed", {
          detail: { chatId: chat.id, isUpdate: true }
        })
      );
    }
  }, [activeChatId]);

  
  // Leave chat explicitly (for UI actions)
  const leaveChat = useCallback(
    (chatId: string, permanent = false) => {
      if (!chatId || !ws) return;

      // Send leave message
      if (ws.connectionStatus === ConnectionStatus.OPEN) {
        ws.sendLeave(chatId);
      }

      // If permanent, update state
      if (permanent && chatId === activeChatId) {
        setActiveChat(null);
      }
    },
    [activeChatId, setActiveChat, ws]
  );

  // Check if a given chatId matches the active chat
  const isActiveChatId = useCallback(
    (chatId: string | null): boolean => {
      if (!chatId) return false;
      return chatId === activeChatId;
    },
    [activeChatId]
  );

  // Handle URL changes - simplified
  useEffect(() => {
    if (chatIdFromUrl && chatIdFromUrl !== activeChatId) {
      localStorage.setItem(ACTIVE_CHAT_KEY, chatIdFromUrl);
      activeChatRef.current = chatIdFromUrl;
    }
  }, [chatIdFromUrl, activeChatId]);

  // Handle visibility and focus changes
  useEffect(() => {
    let lastRefreshTime = 0;
    
    const handleVisibilityAndFocus = () => {
      // Only handle when app becomes visible
      if (document.visibilityState === "visible" && document.hasFocus()) {
        const chatId = activeChatRef.current;
        if (!chatId) return;

        // Prevent excessive refreshes - max once per 20 seconds
        const now = Date.now();
        if (now - lastRefreshTime < 20000) {
          return;
        }
        lastRefreshTime = now;

        // If we're connected, refresh the chat join
        if (ws?.connectionStatus === ConnectionStatus.OPEN) {
          console.log(`Refreshing chat connection: ${chatId}`);
          ws.sendJoin(chatId);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityAndFocus);
    window.addEventListener("focus", handleVisibilityAndFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityAndFocus);
      window.removeEventListener("focus", handleVisibilityAndFocus);
    };
  }, [ws]);

  // Handle WebSocket connection changes
  useEffect(() => {
    let lastJoinTime = 0;
    
    if (ws?.connectionStatus === ConnectionStatus.OPEN) {
      const chatId = activeChatRef.current;
      if (!chatId) return;

      // Prevent excessive join messages - max once per 10 seconds
      const now = Date.now();
      if (now - lastJoinTime < 10000) {
        return;
      }
      lastJoinTime = now;

      console.log(`WebSocket connected, ensuring chat: ${chatId}`);
      ws.sendJoin(chatId);
    }
  }, [ws?.connectionStatus, ws]);

  // Context value
  const contextValue = {
    activeChat,
    activeChatId,
    setActiveChat,
    updateActiveChat,
    isActiveChatId,
    leaveChat,
  };

  return (
    <ActiveChatContext.Provider value={contextValue}>
      {children}
    </ActiveChatContext.Provider>
  );
};